/**
 * Process and filter documents for the folder drawer based on various criteria
 * @param {Array} docs - All documents to process
 * @param {string} searchValue - Current search term
 * @param {Array} searchResults - Search results if a search is active
 * @param {boolean} closedTicketsVisible - Whether to show closed tickets
 * @param {Array} filteredClosedTodayArray - Array of tickets closed today
 * @param {Array} selectedCellOpenTickets - Array of open tickets for the selected cell
 * @param {Array} selectedCellClosedTickets - Array of closed tickets for the selected cell
 * @param {Object} folder - Current selected folder
 * @param {Array} filtersArray - Active filters
 * @param {Array} filteredTicketIds - IDs of tickets matching filters
 * @returns {Array} Filtered and processed documents
 */
export const processDocsForDrawer = (
  docs,
  searchValue,
  searchResults,
  closedTicketsVisible,
  filteredClosedTodayArray,
  selectedCellOpenTickets,
  selectedCellClosedTickets,
  folder,
  filtersArray,
  filteredTicketIds
) => {
  if (!docs || !Array.isArray(docs)) {
    return [];
  }

  let processedDocs = docs;

  // If there's a search value and search results, use search results
  if (searchValue && searchResults && searchResults.length > 0) {
    processedDocs = searchResults;
  }

  // Early return optimization: if we have filteredTicketIds, filter by those first (most restrictive)
  // This reduces the array size before doing other operations
  if (filtersArray && filtersArray.length > 0 && filteredTicketIds && filteredTicketIds.length > 0) {
    const filteredIds = new Set(filteredTicketIds);
    processedDocs = processedDocs.filter((doc) => filteredIds.has(doc.id));
  }

  // Filter by closed ticket visibility with linked cell data
  if (!closedTicketsVisible) {
    // Show open tickets
    if (selectedCellOpenTickets && selectedCellOpenTickets.length > 0) {
      // If we have a selected cell, show only open tickets from that cell
      const openIds = new Set(selectedCellOpenTickets.map(id => 
        typeof id === 'string' ? id : id.id || id
      ));
      processedDocs = processedDocs.filter((doc) => openIds.has(doc.id));
    } else {
      // Otherwise, show all open tickets
      processedDocs = processedDocs.filter(
        (doc) => 
          doc.doc?.pge_status !== 'CLOSED' && 
          doc.doc?.pge_status !== 'COMPLETE'
      );
    }
  } else {
    // Show closed tickets
    if (selectedCellClosedTickets && selectedCellClosedTickets.length > 0) {
      // If we have a selected cell, show only closed tickets from that cell
      const closedIds = new Set(selectedCellClosedTickets.map(id => 
        typeof id === 'string' ? id : id.id || id
      ));
      processedDocs = processedDocs.filter((doc) => closedIds.has(doc.id));
    } else if (filteredClosedTodayArray && filteredClosedTodayArray.length > 0) {
      const closedIds = new Set(filteredClosedTodayArray.map(id => 
        typeof id === 'string' ? id : id.id || id
      ));
      processedDocs = processedDocs.filter((doc) => closedIds.has(doc.id));
    } else {
      processedDocs = processedDocs.filter(
        (doc) => 
          doc.doc?.pge_status === 'CLOSED' || 
          doc.doc?.pge_status === 'COMPLETE'
      );
    }
  }

  // Filter by folder if one is selected
  if (folder && folder.value && folder.value !== '') {
    processedDocs = processedDocs.filter(
      (doc) => doc.doc?.pge_folderID === folder.value
    );
  }

  return processedDocs;
};
